import { ContextMetadata, retry } from '@lidea/shared/data-access/core';
import { Measures } from '@lidea/shared/util/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { UploadPictureDto } from '../../apis/zoning-api';
import { analysisKeys } from '../../keys/analysis-keys';
import { isMissingCropPictureError } from './missing-crop-picture-error';
import { ensurePendingCropPicture } from './pending-crop-pictures';
import { uploadCropPicturePersisted } from './upload-crop-picture-persisted';

export const uploadCropPictureOptions = {
  mutationKey: ['uploadCropPicture'],
  mutationFn: uploadCropPicturePersisted,
  retry: (failureCount: number, error: Error) => {
    if (isMissingCropPictureError(error)) {
      return false;
    }
    return typeof retry === 'function' ? retry(failureCount, error) : false;
  },
  // Pause (do not fire upload) while app offline mode is active — manual HL or no network.
  networkMode: 'online' as const,
  scope: {
    id: 'measure',
  },
  // Runs even when paused offline — persist bytes before the process can die.
  onMutate: async (variables: UploadPictureDto) => {
    await ensurePendingCropPicture(variables);
  },
};

export const useUploadCropPicture = ({
  fieldId,
  measureZone,
  analysisId,
  measureId,
}: {
  fieldId: string;
  measureZone: number;
  analysisId?: number;
  measureId: number;
}) => {
  const { t } = useTranslation(['analysis'], { useSuspense: false });
  const queryClient = useQueryClient();
  const mutation = useMutation({
    ...uploadCropPictureOptions,
    onMutate: async (variables) => {
      await ensurePendingCropPicture(variables);

      let pointId: number | undefined;
      if (analysisId != null) {
        const measures = queryClient.getQueryData<Measures>(
          analysisKeys.measures(analysisId).queryKey
        );
        pointId = Object.values(measures ?? {}).find(
          (measure) => measure.id === measureId
        )?.point.id;
      }

      const metadata: ContextMetadata = {
        title: `${t('analysis:picture_mutation_title')} ${measureZone}`,
        fieldId: Number(fieldId),
        measureId,
        pointId,
      };
      return {
        metadata,
      };
    },
    onSuccess: () => {
      if (analysisId != null) {
        queryClient.invalidateQueries({
          queryKey: analysisKeys.measures(analysisId).queryKey,
        });
      } else {
        queryClient.invalidateQueries({ queryKey: ['measures'] });
      }
    },
  });

  return mutation;
};

export default useUploadCropPicture;
